import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';
import laceyImg from '../assets/Lacey Logo 2024 FINAL 2.png';
import equitySvg from '../assets/Equity.svg';
import accessibilitySvg from '../assets/Accessibility.svg';
import livabilitySvg from '../assets/Livability.svg';
import educationSvg from '../assets/Education.svg';
import economySvg from '../assets/economy.svg';
import environmentSvg from '../assets/Environment.svg';

import PDFMetricCard from "../components/pdf/PDFMetricCard";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { useContext, useEffect, useState } from "react";
import { categorizeData, getIndexMetricDataPDF, getMetricGroupChartId, getSortedMetrics, getSvgMap, orgId } from "../utils/dashboardUtils";
import LoadingIndicator from "../components/LoadingIndicator";
import TitleBanner from "../components/pdf/TitleBanner";
import DisaggregateMetricCard from "../components/DisaggregateMetricCard";
import AgeGenderChart from "../components/charts/AgeGenderChart";
import PDFRaceEthnicityChart from "../components/pdf/PDFRaceEthnicityChart";
import PDFBarChart from "../components/pdf/PDFBarChart";
import UnlabelledComparisonChart from "../components/charts/UnlabelledComparisonChart";
import { indicatorsEnv } from "../utils/environment";


interface MetricsModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    geoDetails: GeoDetails[];
    metadata: MetricMetadata[] | null;
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    legendUrl: string;
    selectedArea: string;
    siteUrl: string;
    selectionActive: boolean;
}

const MetricsModal: React.FC<MetricsModalProps> = ({ isOpen, onClose, metadata, geoDetails, geoTag, pdfUrl, logoUrl, legendUrl, siteUrl, selectedArea, selectionActive }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    console.log('data:', disaggregatedData);

    // Categorize the disaggregated data
    const categorizedData = categorizeData(disaggregatedData);

    // Get sorted metrics for each dashboard panel
    const sortedDemographicMetrics: DisaggregatedMetric[][] = getSortedMetrics('Demographics', categorizedData);
    const sortedAccessibilityMetrics: DisaggregatedMetric[][] = getSortedMetrics('Accessibility', categorizedData);
    const sortedLivabilityMetrics: DisaggregatedMetric[][] = getSortedMetrics('Livability', categorizedData);
    const sortedEducationMetrics: DisaggregatedMetric[][] = getSortedMetrics('Education', categorizedData);
    const sortedEconomyMetrics: DisaggregatedMetric[][] = getSortedMetrics('Economy', categorizedData);
    const sortedEnvironmentMetrics: DisaggregatedMetric[][] = getSortedMetrics('Environment', categorizedData);

    // Get index metric data for each dashboard panel
    const indexMetricDataDemographic = getIndexMetricDataPDF('Demographics', metadata, disaggregatedData);
    const indexMetricDataAccessibility = getIndexMetricDataPDF('Accessibility', metadata, disaggregatedData);
    const indexMetricDataLivability = getIndexMetricDataPDF('Livability', metadata, disaggregatedData);
    const indexMetricDataEducation = getIndexMetricDataPDF('Education', metadata, disaggregatedData);
    const indexMetricDataEconomy = getIndexMetricDataPDF('Economy', metadata, disaggregatedData);
    const indexMetricDataEnvironment = getIndexMetricDataPDF('Environment', metadata, disaggregatedData);

    // Check if city lod Population metric is present, and filter out block group lod
    const flatDemographicMetrics = sortedDemographicMetrics.flat();
    const hasCityPop = flatDemographicMetrics.some(metric => metric.id === 1184);
    const filteredDemographicMetrics = hasCityPop ? flatDemographicMetrics.filter(metric => metric.id !== 1133) : flatDemographicMetrics;
    

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "DashboardMetrics";  // Set your desired title here
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[800px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    {/* Print and Close buttons */}
                    <div className="flex justify-center items-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-[#0A5F75] border border-[#0A5F75] text-white py-1 pl-7 pr-8 rounded-md"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-3 h-3 mr-2 mt-[0.5px]" />
                            <p className="text-xs font-bold">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-[#0A5F75] py-1 px-8 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-xs text-[#0A5F75] font-bold">Close</p>
                        </button>
                    </div>

                    {/* Page 1 */}
                    <div className="flex flex-col page">
                        <div className="flex flex-col items-center justify-center relative">
                            <div className="w-full h-auto p-4 flex justify-between items-center bg-[#4D4D4D] rounded-md">
                                <img src={laceyImg} alt='City of Lacey Logo'/>
                                <div className="flex flex-col items-start justify-start mx-4 w-full">
                                    <p className="text-white font-bold text-[18px]">City of Lacey, WA</p>
                                    <p className="text-xs text-white">{selectedArea || 'City of Lacey'}</p>
                                    <p className="text-xs text-white font-bold">{siteUrl || ''}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="relative inline-block">
                                {pdfUrl && <img src={pdfUrl} className="h-auto w-auto" alt="Esri Map Image" /> }
                                {/* Removing legend for now due to performance concerns */}
                                { <div className="flex justify-between">
                                    { legendUrl && <img src={legendUrl} className="h-[188px] w-[220px] ml-6 mt-[-5rem]" alt="Esri Legend Image" /> }
                                    { logoUrl && <img src={logoUrl} className="h-10 w-30 mr-8 mt-4" alt="Esri Logo Image" /> }
                                </div> }
    
                            </div>
                        </div>
                        {/* Demographics Metrics */}
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Equity"/>
                                <p className="text-lacey-text mx-6 text-xs mt-4">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {filteredDemographicMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}        
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Demographics Charts */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Equity"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    <div className="col-span-2 w-full px-2">
                                        {categorizedData.Demographics.filter(metric => [getMetricGroupChartId("Demographics", "race")].includes(metric.id))
                                        .map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                title={"Race & Ethnicity"}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                        ))}
                                        <PDFRaceEthnicityChart indicators={disaggregatedData} title="Race and Ethnicity" tab="Demographics" chartName="race" height="h-80" barColor="#00A18A" reverseOrder={false} topFive={false}/>
                                    </div>
                                    <div className="col-span-2 row-span-2 w-full px-2 mb-4">
                                        <DisaggregateMetricCard
                                            key={1147}
                                            id={1147}
                                            title={"Age and Sex"}
                                            svg={getSvgMap(1147)}
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            showMetadataIcon={false}
                                        />
                                        <AgeGenderChart indicators={disaggregatedData} title="Age and Sex" height="h-80"/>
                                    </div>
                                    <div className="col-span-2 w-full px-2 my-4">
                                    {categorizedData.Demographics.filter(metric =>  
                                            [getMetricGroupChartId('Demographics', "immigration")].includes(metric.id) // Top Countries of Immigration
                                        ).map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                title={metric.name}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                        ))}
                                        <PDFBarChart indicators={disaggregatedData} title="Top Countries of Immigration" tab={'Demographics'} chartName="immigration" height="h-48" barColor="#00A18A" reverseOrder={false} topFive={true}/>
                                    </div> 
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 3: Accessibility */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataAccessibility} geoDetails={geoDetails} geoTag={geoTag} svg={accessibilitySvg} text="Accessiblity"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedAccessibilityMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 4: Livability */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataLivability} geoDetails={geoDetails} geoTag={geoTag} svg={livabilitySvg} text="Livability"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedLivabilityMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}

                                    {/* Crime */}
                                    { categorizedData.Livability.some(metric => metric.id === 1183) && (
                                        <div className="col-span-3 w-[440px] pr-4 mb-4">
                                            <div className="bg-anti-flash pb-2">
                                                {categorizedData.Livability.filter(metric => 
                                                    [getMetricGroupChartId("Livability", "crime")].includes(metric.id) // Crime
                                                ).map(metric => (
                                                    <DisaggregateMetricCard
                                                        key={metric.id}
                                                        id={metric.id}
                                                        title={metric.name}
                                                        svg={getSvgMap(metric.id)}
                                                        disaggData={disaggregatedData}
                                                        geoDetails={geoDetails}
                                                        geoTag={geoTag}
                                                        selectionActive={selectionActive}
                                                        showMetadataIcon={false}
                                                    />
                                                ))}

                                                <div className="relative w-full">
                                                    {/* Vertical red line at 200px, covering both bars */}
                                                    <div className="absolute top-4 bottom-2 w-[2px]" style={{ left: '238px', backgroundColor: 'rgba(239, 68, 68, 0.4)' }}></div>

                                                    <UnlabelledComparisonChart indicators={disaggregatedData} title="Crime" tab="Livability" chartName="crime" height="h-24" barColor="#00A18A"/>

                                                    <p className="text-color-cai-gray text-[9px] absolute -bottom-2" style={{ left: '202px' }}>National Average</p>
                                                </div>
                                                    
                                                <p className="text-[#4D4D4D] text-[11px] ml-6 my-4">{categorizedData.Livability.filter(metric => 
                                                    [getMetricGroupChartId("Livability", "crime")].includes(metric.id) // Crime Risk
                                                ).map(metric => metric.versions[0].unit_descriptor)}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 5: Economy + Education */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataEconomy} geoDetails={geoDetails} geoTag={geoTag} svg={economySvg} text="Economy"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedEconomyMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataEducation} geoDetails={geoDetails} geoTag={geoTag} svg={educationSvg} text="Education"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedEducationMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 6: Environment */}
                    <div className="page">
                        <div className="shadow-md rounded-md my-8">
                            <TitleBanner metric={indexMetricDataEnvironment} geoDetails={geoDetails} geoTag={geoTag} svg={environmentSvg} text="Environment"/>
                            <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                {sortedEnvironmentMetrics.flat().map((metric) => (
                                    <PDFMetricCard
                                    key={metric.id}
                                    metric={metric}
                                    width="w-full"
                                    svg={getSvgMap(metric.id)}
                                    showMetadataIcon={false}
                                    disaggData={disaggregatedData}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricsModal;